import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import api, { errorMessage } from "../../lib/api";
import { formatDateTime } from "../../lib/missions";
import { RESULT_META, groupAssets } from "../../lib/equipment";
import { AssetDialog, IssueDialog, RetireDialog } from "./EquipmentDialogs";
import IssueDetailSheet, { IssueStatusBadge, SeverityBadge } from "./IssueDetailSheet";
import { ConditionDot } from "./MissionEquipmentCard";
import { useEquipmentCategories } from "./useEquipmentCategories";
import { ReceiptPreviewDialog } from "../finance/ReceiptPreview";
import DeleteWithReasonDialog from "../DeleteWithReasonDialog";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Skeleton } from "../ui/skeleton";
import { ResponsiveDropdownMenu, ResponsiveDropdownMenuContent, ResponsiveDropdownMenuItem, ResponsiveDropdownMenuTrigger } from "../ui/responsive-dropdown-menu";
import { ChevronDown, MoreHorizontal, Plus, Printer } from "lucide-react";

// « Matériel » tab of a pharmacy (equipment:view_all / manage_all): the whole park with its serial numbers, the history of the checks
// made on every mission, and the issues. Editing, retiring and opening an issue outside a mission need equipment:manage_all.
export default function PharmacyEquipmentTab({ prospectId, userId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [dialog, setDialog] = useState(null); // {kind: "add"|"edit"|"retire"|"issue", asset?}
  const [deleting, setDeleting] = useState(null);
  const [issueId, setIssueId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [showChecks, setShowChecks] = useState(false);
  const categories = useEquipmentCategories();

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get(`/prospects/${prospectId}/equipment`);
      setData(d);
      setError("");
    } catch (e) {
      setError(errorMessage(e, "Impossible de charger le matériel de la pharmacie."));
    }
  }, [prospectId]);
  useEffect(() => { load(); }, [load]);

  const confirmDelete = async (reason) => {
    const target = deleting;
    try {
      await api.delete(`/equipment/${target.id}`, { data: { reason } });
      toast.success("Équipement mis à la corbeille");
      load();
    } catch (e) {
      toast.error(errorMessage(e, "Impossible de supprimer l'équipement."));
    } finally {
      setDeleting(null);
    }
  };

  if (error && !data) return <Card className="p-5 text-sm text-slate-600 flex items-center gap-2 flex-wrap">{error}<Button variant="outline" size="sm" onClick={load}>Réessayer</Button></Card>;
  if (!data) return <Card className="p-5"><Skeleton className="h-24 w-full" /></Card>;

  const caps = data.capabilities;
  const { groups, retired } = groupAssets(data.assets);
  const close = () => setDialog(null);
  const activeIssues = data.issues.filter((i) => ["open", "in_progress"].includes(i.status));
  const closedIssues = data.issues.filter((i) => !["open", "in_progress"].includes(i.status));
  const byId = Object.fromEntries(data.assets.map((a) => [a.id, a]));

  return (
    <div className="space-y-4" data-testid="pharmacy-equipment">
      <Card className="p-5 space-y-4">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2"><Printer size={16} className="text-slate-400" />Parc matériel</h3>
          <div className="flex gap-2">
            {caps.open_issue && <Button variant="outline" size="sm" onClick={() => setDialog({ kind: "issue" })} data-testid="pharmacy-equipment-new-issue">Signaler une anomalie</Button>}
            {caps.add_asset && <Button size="sm" onClick={() => setDialog({ kind: "add" })} data-testid="pharmacy-equipment-add"><Plus size={14} className="mr-1" />Ajouter</Button>}
          </div>
        </div>
        {groups.length === 0 ? (
          <p className="text-sm text-slate-500" data-testid="pharmacy-equipment-empty">Aucun matériel recensé. Il est ajouté pendant les missions ou par un gestionnaire du matériel.</p>
        ) : groups.map((g) => (
          <section key={g.label}>
            <h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1.5">{g.label}</h4>
            <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
              {g.items.map((a) => (
                <li key={a.id} className="p-3 flex items-start gap-3" data-testid={`pharmacy-equipment-asset-${a.id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900 break-words">{[a.manufacturer, a.model].filter(Boolean).join(" ") || a.category_label}</div>
                    <div className="text-xs text-slate-500 break-words">{a.location || "Emplacement non précisé"}{a.serial_number ? ` · N° ${a.serial_number}` : ""}</div>
                    {a.note && <div className="text-xs text-slate-600 mt-0.5 whitespace-pre-line">{a.note}</div>}
                    <div className="mt-1 flex flex-wrap items-center gap-1.5">
                      <ConditionDot asset={a} />
                      {a.last_check && <span className="text-2xs text-slate-500">le {formatDateTime(a.last_check.at)}</span>}
                    </div>
                  </div>
                  {(caps.edit_asset || caps.retire_asset || caps.delete_asset) && (
                    <ResponsiveDropdownMenu>
                      <ResponsiveDropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-10 w-10 shrink-0" aria-label={`Actions : ${a.label}`} data-testid={`pharmacy-equipment-more-${a.id}`}><MoreHorizontal size={16} /></Button>
                      </ResponsiveDropdownMenuTrigger>
                      <ResponsiveDropdownMenuContent align="end">
                        {caps.edit_asset && <ResponsiveDropdownMenuItem onClick={() => setDialog({ kind: "edit", asset: a })}>Modifier</ResponsiveDropdownMenuItem>}
                        {caps.retire_asset && <ResponsiveDropdownMenuItem className="text-red-700" onClick={() => setDialog({ kind: "retire", asset: a })}
                          data-testid={`pharmacy-equipment-retire-${a.id}`}>Retirer du parc</ResponsiveDropdownMenuItem>}
                        {caps.delete_asset && <ResponsiveDropdownMenuItem className="text-red-700" onClick={() => setDeleting(a)}
                          data-testid={`pharmacy-equipment-delete-${a.id}`}>Supprimer…</ResponsiveDropdownMenuItem>}
                      </ResponsiveDropdownMenuContent>
                    </ResponsiveDropdownMenu>
                  )}
                </li>
              ))}
            </ul>
          </section>
        ))}
        {retired.length > 0 && (
          <details className="text-sm">
            <summary className="cursor-pointer text-slate-600">Retirés ({retired.length})</summary>
            <ul className="mt-2 space-y-1">
              {retired.map((a) => <li key={a.id} className="text-slate-500">{a.label} — {a.retired_reason}{a.retired_at ? ` (${formatDateTime(a.retired_at)})` : ""}</li>)}
            </ul>
          </details>
        )}
      </Card>

      <Card className="p-5 space-y-3">
        <h3 className="text-base font-semibold text-slate-900">Anomalies</h3>
        {data.issues.length === 0 ? <p className="text-sm text-slate-500">Aucune anomalie pour cette pharmacie.</p> : (
          <ul className="divide-y divide-slate-100" data-testid="pharmacy-equipment-issues">
            {[...activeIssues, ...closedIssues].map((i) => (
              <li key={i.id}>
                <button type="button" onClick={() => setIssueId(i.id)} className="w-full py-2.5 text-left flex items-start gap-2 hover:bg-slate-50 rounded" data-testid={`pharmacy-equipment-issue-${i.id}`}>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-slate-900 break-words">{i.title}</span>
                    <span className="block text-xs text-slate-500">{i.number}{i.asset_label ? ` · ${i.asset_label}` : ""} · {i.responsible_name}</span>
                  </span>
                  <span className="flex gap-1 shrink-0"><SeverityBadge severity={i.severity} /><IssueStatusBadge status={i.status} /></span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card className="p-5">
        <button type="button" onClick={() => setShowChecks((v) => !v)} aria-expanded={showChecks} className="text-sm font-semibold text-slate-900 inline-flex items-center gap-1"
          data-testid="pharmacy-equipment-checks-toggle">
          <ChevronDown size={14} className={showChecks ? "rotate-180" : ""} />Historique des contrôles ({data.checks.length})
        </button>
        {showChecks && (
          <ol className="mt-3 space-y-2.5" data-testid="pharmacy-equipment-checks">
            {data.checks.map((c) => (
              <li key={c.id} className={`text-sm ${c.state === "replaced" ? "text-slate-400" : "text-slate-800"}`}>
                <div className={c.state === "replaced" ? "line-through" : ""}>
                  {byId[c.asset_id]?.label || "Équipement"} : {RESULT_META[c.result].label}{c.measures ? ` — ${c.measures}` : ""}
                </div>
                <div className="text-xs text-slate-500">
                  {formatDateTime(c.checked_at)} · {c.performed_by.name}{c.recorded_by_name !== c.performed_by.name ? ` (saisi par ${c.recorded_by_name})` : ""}
                  {c.mission_reference && <> · <Link className="text-jamora hover:underline" to={`/missions/${c.mission_id}`}>{c.mission_reference}</Link></>}
                  {c.reason && ` · corrigé : ${c.reason}`}
                </div>
                {c.files.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-1">
                    {c.files.map((f) => (
                      <button key={f.id} type="button" className="text-xs text-jamora hover:underline" onClick={() => setPreview({ source: { key: `file:${f.id}`, path: `/files/${f.id}` }, name: f.name })}>
                        {f.name}
                      </button>
                    ))}
                  </div>
                )}
              </li>
            ))}
          </ol>
        )}
      </Card>

      <AssetDialog open={dialog?.kind === "add"} onOpenChange={close} categories={categories} url={`/prospects/${prospectId}/equipment`} serialAllowed
        onDone={(d) => { setData(d); close(); }} />
      <AssetDialog open={dialog?.kind === "edit"} onOpenChange={close} categories={categories} asset={dialog?.asset} url={dialog?.asset ? `/equipment/${dialog.asset.id}` : ""}
        method="patch" serialAllowed onDone={() => { close(); load(); }} />
      <DeleteWithReasonDialog
        target={deleting} testId="delete-asset" onClose={() => setDeleting(null)} onConfirm={confirmDelete}
        title={`Supprimer « ${deleting?.label || ""} » ?`}
        description="Il n'a jamais été contrôlé ni signalé. Il va dans la corbeille : vous pourrez le restaurer pendant 30 jours."
      />
      <RetireDialog open={dialog?.kind === "retire"} onOpenChange={close} asset={dialog?.asset} onDone={() => { close(); load(); }} />
      <IssueDialog open={dialog?.kind === "issue"} onOpenChange={close} prospectId={prospectId} assets={data.assets} candidates={data.responsible_candidates}
        userId={userId} onDone={(issue) => { close(); load(); setIssueId(issue.id); }} />
      <IssueDetailSheet issueId={issueId} onOpenChange={(open) => { if (!open) setIssueId(null); }} onChanged={load} />
      <ReceiptPreviewDialog receipt={preview} onClose={() => setPreview(null)} description="Photo jointe au contrôle." />
    </div>
  );
}
